import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import { useTheme } from '@material-ui/core/styles';
import { ROUNDED_CONTROL_RADIUS } from 'components/theme/controlRadius';
import {
  IS_IOS,
  getBankOverlayZIndex,
} from 'scenes/ProjectBank/constants/bankOverlay';

/**
 * How much of the page stays in sight above a sheet at its tallest,
 * Material's margin for a modal bottom sheet: it reads as a sheet over the
 * files, and a tap there closes it.
 */
export const BANK_SHEET_TOP_GAP = 72;

const noop = () => {};

/**
 * Whether a touch that starts on `target` starts inside a part of the sheet
 * that scrolls sideways (a wide table or a code block in an AI reply), below
 * `sheet`.
 * @param {Element} target
 * @param {Element} sheet
 * @returns {boolean}
 */
const startsInSidewaysScroller = (target, sheet) => {
  for (let node = target; node && node !== sheet; node = node.parentElement) {
    if (node.scrollWidth > node.clientWidth) {
      const { overflowX } = window.getComputedStyle(node);
      if (overflowX === 'auto' || overflowX === 'scroll') return true;
    }
  }
  return false;
};

/**
 * Leaves a touch that starts in a sideways scroller to the browser. MUI's
 * swipe cancels the first move of any touch on the sheet that goes more
 * sideways than down, whichever edge the sheet opens from, which would keep
 * a wide table from scrolling; it skips a touch that is marked as another
 * handler's (`muiHandled`), and this listener on the sheet sees the touch
 * before MUI's on the document does.
 * @param {TouchEvent} event
 */
const handleSheetTouchStart = (event) => {
  if (startsInSidewaysScroller(event.target, event.currentTarget)) {
    event.muiHandled = true;
  }
};

/**
 * The phone's (xs) bottom sheet, for the bank's parts that open over the
 * files instead of beside them: the filters and the sort (BankFilterSheet)
 * and the AI panel (BankAiSheet). One container, so the two open, look and
 * close alike.
 *
 * A modal SwipeableDrawer from the bottom edge with rounded top corners and
 * a grab handle that says it can be swiped away. It is as tall as what it
 * holds, and at most the screen less BANK_SHEET_TOP_GAP; a part of the
 * content that has to take all of that height asks for it itself (a flex
 * basis past the limit, which the sheet clamps). It closes the way a phone
 * expects, by a swipe down, a tap on the page above it or Escape, and the
 * caller adds a visible way out of its own ("Valmis", a close button) for
 * whoever cannot swipe. MUI's swipe lets a scrolled part inside scroll back
 * up before a swipe down closes the sheet, as long as no box between that
 * part and the sheet clips sideways (`overflow-x: hidden`) or is absolutely
 * positioned: MUI forgets the scrolled parts it found inside such a box. A
 * touch that starts in a part that scrolls sideways is left to the browser
 * altogether, so a wide table scrolls (see handleSheetTouchStart).
 *
 * Only its button opens it: a swipe up from the bottom edge is the phone's
 * own gesture, and MUI's strip along that edge for it would swallow the taps
 * there. It lies over the app bar and under the dialogs, at the level of the
 * bank's other overlays (constants/bankOverlay), so a dialog it opens paints
 * over it; a sheet that opens over a dialog (the photo viewer's tag sheets)
 * sets a `zIndex` above the dialogs instead. What it holds is mounted while
 * it is open and dropped once it has slid out.
 *
 * The props not listed go to the drawer's root, the element that holds the
 * backdrop and the sheet (a `data-*` attribute that names it, for one); they
 * cannot replace what the sheet sets itself.
 * @param {Object} props
 * @param {string} props.id DOM id of the sheet, for the opener's `aria-controls`
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {string} props.labelledBy id of the sheet's title
 * @param {'base'|'raised'} [props.surface='base'] the bank surface it paints: the page's own, or the raised one of the panels
 * @param {boolean} [props.disableEnforceFocus=false] let the focus leave it, for what it opens outside itself (a calendar portaled to the body)
 * @param {number} [props.zIndex] its level, in place of the bank's overlays' one
 * @param {React.ReactNode} [props.children]
 */
export function BankBottomSheet({
  id,
  open,
  onClose,
  labelledBy,
  surface,
  disableEnforceFocus,
  zIndex,
  children,
  ...other
}) {
  const theme = useTheme();
  const { projectBankSurface, projectBankSurfaceRaised } = theme.component;

  // MUI's drawer keeps the sheet's ref for itself, so the sheet is reached
  // through the grab handle, its first child, which mounts and unmounts with
  // it.
  const sheetRef = useRef(null);
  const handleRef = useCallback((handle) => {
    if (sheetRef.current) {
      sheetRef.current.removeEventListener('touchstart', handleSheetTouchStart);
      sheetRef.current = null;
    }
    if (handle && handle.parentElement) {
      sheetRef.current = handle.parentElement;
      sheetRef.current.addEventListener('touchstart', handleSheetTouchStart);
    }
  }, []);

  return (
    <SwipeableDrawer
      {...other}
      anchor="bottom"
      open={open}
      onOpen={noop}
      onClose={onClose}
      disableSwipeToOpen
      disableBackdropTransition={!IS_IOS}
      ModalProps={{
        disableEnforceFocus,
        // Above the app bar and under the dialogs, at the level of the
        // sidebar drawer, unless the caller sets a level of its own.
        style: { zIndex: zIndex ?? getBankOverlayZIndex(theme) },
      }}
      PaperProps={{
        id,
        role: 'dialog',
        'aria-labelledby': labelledBy,
        tabIndex: -1,
        'data-marquee-ignore': 'true',
        style: {
          maxHeight: `calc(100% - ${BANK_SHEET_TOP_GAP}px)`,
          borderTopLeftRadius: ROUNDED_CONTROL_RADIUS,
          borderTopRightRadius: ROUNDED_CONTROL_RADIUS,
          backgroundColor: (surface === 'raised'
            ? projectBankSurfaceRaised
            : projectBankSurface
          ).backgroundColor,
        },
      }}
    >
      {/* The grab handle: says the sheet can be swiped away. */}
      <div
        ref={handleRef}
        aria-hidden="true"
        data-bank-sheet-handle="true"
        style={{
          flexShrink: 0,
          width: 32,
          height: 4,
          borderRadius: 2,
          margin: `${theme.spacing(1)}px auto 0`,
          backgroundColor: theme.palette.text.disabled,
        }}
      />
      {children}
    </SwipeableDrawer>
  );
}

BankBottomSheet.propTypes = {
  /** DOM id of the sheet; the opener names it in `aria-controls`. */
  id: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Id of the sheet's title, which names the dialog. */
  labelledBy: PropTypes.string.isRequired,
  surface: PropTypes.oneOf(['base', 'raised']),
  disableEnforceFocus: PropTypes.bool,
  /** Its level, for a sheet over a dialog; the bank's overlays' level by default. */
  zIndex: PropTypes.number,
  children: PropTypes.node,
};

BankBottomSheet.defaultProps = {
  surface: 'base',
  disableEnforceFocus: false,
};

export default BankBottomSheet;
